/**
 * Build the worksheets of the client-side Excel export from the page state.
 * Only sheets with actual data are emitted; nothing is fabricated. Pure.
 */

export function buildExportSheets({ country, quarters, rows, originals, result, baselineMap, v2, diagnostics, meta, scenarioHash, scenarioLabel }) {
  const sheets = [];
  const skipped = [];

  // Inputs: indicator × quarters, with the consensus value next to each edited value
  if (rows && rows.length && quarters && quarters.length) {
    const head = ["Indicator", ...quarters, ...quarters.map((q) => `${q} consensus`)];
    const body = rows
      .filter((r) => !r.header)
      .map((r, i) => {
        const orig = (originals || []).find((o) => o.name === r.name) || originals?.[i];
        return [r.name, ...r.data.map(num), ...(orig ? orig.data.map(num) : quarters.map(() => null))];
      });
    sheets.push({ name: "Inputs", rows: [head, ...body] });
  } else skipped.push("Inputs");

  // Forecast: indicator × dates (result) + baseline rows when available
  if (result && result.dates && result.dates.length) {
    const head = ["Indicator", "Series", ...result.dates];
    const body = [];
    for (const r of result.rows) {
      body.push([r.name, "Forecast (shown result)", ...r.data.map(num)]);
      const b = baselineMap && baselineMap[r.name];
      if (b) body.push([r.name, "Janus baseline", ...result.dates.map((d) => num(b[d]))]);
    }
    sheets.push({ name: "Forecast", rows: [head, ...body] });
  } else skipped.push("Forecast");

  // Intervals
  if (v2 && Array.isArray(v2.intervals) && v2.intervals.length) {
    const head = ["Series", "Scenario", "Level", "Bound", "Method", "Sample from", "Sample to", ...v2.dates];
    const body = [];
    for (const b of v2.intervals) {
      const s = b.sample || {};
      body.push([b.series, b.scenario, b.level, "lower", b.method, s.from || null, s.to || null, ...b.lower.map(num)]);
      body.push([b.series, b.scenario, b.level, "upper", b.method, s.from || null, s.to || null, ...b.upper.map(num)]);
    }
    sheets.push({ name: "Intervals", rows: [head, ...body] });
  } else skipped.push("Intervals");

  // Metrics
  if (diagnostics && Array.isArray(diagnostics.outOfSample) && diagnostics.outOfSample.length) {
    const keys = ["rmse", "mae", "mape", "bias", "theilURandomWalk", "theilUConsensus", "hitRate", "coverage80", "coverage95"];
    const head = ["Series", "Horizon (weeks)", "n", "Origins", "Method", ...keys];
    const body = diagnostics.outOfSample.map((o) => [o.series, o.horizon, o.n ?? null, o.vintages ?? null, o.method || null, ...keys.map((k) => num(o[k]))]);
    const eqHead = ["Series", "Spec", "Estimator", "Sample start", "Sample end", "n obs", "R2", "Adj R2", "Reg. SE", "Durbin-Watson"];
    const eqBody = (diagnostics.equations || []).map((e) => [e.series, e.spec, e.estimator, e.sampleStart || null, e.sampleEnd || null, e.nObs ?? null, num(e.inSample?.r2), num(e.inSample?.r2Adj), num(e.inSample?.regressionSe), num(e.inSample?.durbinWatson)]);
    sheets.push({ name: "Metrics", rows: [head, ...body, [], ["In-sample"], eqHead, ...eqBody] });
  } else skipped.push("Metrics");

  // Drivers
  if (v2 && v2.drivers && Object.keys(v2.drivers).length) {
    const head = ["Series", "Week", "Date", "Basis", "Baseline", "Alternative", "Input", "Contribution", "Unit", "Residual", "Tolerance"];
    const body = [];
    for (const d of Object.values(v2.drivers)) {
      if (!d.contributions || !d.contributions.length) {
        body.push([d.series, d.week, d.date || null, d.basis, num(d.baselineValue), num(d.alternativeValue), "(no contributions)", null, null, num(d.residual), num(d.tolerance)]);
        continue;
      }
      for (const c of d.contributions) body.push([d.series, d.week, d.date || null, d.basis, num(d.baselineValue), num(d.alternativeValue), c.input, num(c.contribution), c.unit || null, num(d.residual), num(d.tolerance)]);
    }
    sheets.push({ name: "Drivers", rows: [head, ...body] });
  } else skipped.push("Drivers");

  // Coefficients (when provided)
  if (diagnostics && (diagnostics.equations || []).some((e) => e.coefficients && e.coefficients.length)) {
    const head = ["Series", "Spec", "Estimator", "Input", "Transform", "Estimate", "Std. err.", "t", "Expected sign"];
    const body = [];
    for (const e of diagnostics.equations) for (const c of e.coefficients || []) body.push([e.series, e.spec, e.estimator, c.input, c.transform || null, num(c.estimate), num(c.stdErr), num(c.tStat), c.expectedSign || null]);
    sheets.push({ name: "Coefficients", rows: [head, ...body] });
  }

  // Metadata: always present
  const m = meta || {};
  const metaRows = [
    ["key", "value"],
    ["country", country || null],
    ["scenario", scenarioLabel || null],
    ["scenarioHash", scenarioHash || null],
    ["dataAsOf", m.dataAsOf || "not provided"],
    ["modelEstimatedAt", m.modelEstimatedAt || "not provided"],
    ["modelVariant", m.modelVariant || "not provided"],
    ["modelVersion", m.modelVersion || "not provided"],
    ["illustrative", m.illustrative ? "TRUE — fixture data, not a live forecast" : "false"],
    ["exportedAt", new Date().toISOString()],
    ["sheetsOmitted", skipped.length ? skipped.join(", ") + " (no data available)" : "none"],
  ];
  if (v2 && v2.meta && v2.meta.engine) metaRows.push(["engine", v2.meta.engine]);
  if (diagnostics && diagnostics.lineage) for (const l of diagnostics.lineage) metaRows.push([`source · ${l.series}`, `${l.source}${l.identifier ? " · " + l.identifier : ""} · last ${l.lastObservation || "n/a"}`]);
  sheets.push({ name: "Metadata", rows: metaRows });

  return { sheets, skipped };
}

function num(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : String(v);
}
